import { type LifePoint } from '@/api/types';

export interface LifeRow {
  turn: number;
  me: number;
  opp: number;
}

const STARTING_LIFE = 20;

/**
 * Turn-by-turn life totals for both seats, gaps filled from the last known
 * value. The log only reports a total when it changes, so a turn with no entry
 * for a seat means that seat's life held — not that it is unknown.
 *
 * Within one turn the lowest reported total wins: damage lands mid-turn and the
 * end of the turn is what the next turn inherits.
 */
export function lifeChartRows(points: readonly LifePoint[]): LifeRow[] {
  if (points.length === 0) return [];

  const lowest = new Map<number, { me?: number; opp?: number }>();
  let first = Number.POSITIVE_INFINITY;
  let last = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    if (point.turn < first) first = point.turn;
    if (point.turn > last) last = point.turn;
    const slot = lowest.get(point.turn) ?? {};
    const seen = slot[point.side];
    if (seen === undefined || point.life < seen) slot[point.side] = point.life;
    lowest.set(point.turn, slot);
  }

  const rows: LifeRow[] = [];
  let me = STARTING_LIFE;
  let opp = STARTING_LIFE;
  for (let turn = first; turn <= last; turn++) {
    const slot = lowest.get(turn);
    if (slot?.me !== undefined) me = slot.me;
    if (slot?.opp !== undefined) opp = slot.opp;
    rows.push({ turn, me, opp });
  }
  return rows;
}
